import React, { useEffect, useRef, useState } from "react";
import { FormContext, iChoice } from "../../forms";
import { LabelContainer } from "../controls/styled/container";
import { NumberInput, Select } from "../controls";
import StyledRowContainer, {
    StyledRowContainerItem,
    StyledRowError
} from "../controls/styled/row-container";

import Container from "./container";

import { iControlProps } from "../types";

type Props = iControlProps & {
    id: string;
    type: string;
    context?: FormContext;
    isRequired?: string;
    error?: string;
    errorMessage?: string;
    inputs: {
        customLabel?: string;
        defaultValue: string;
        id: string;
        label?: string;
        name?: string;
        placeholder?: string;
    }[];
    timeFormat?: string;
    timeFormatChoices?: Array<string>;
};

const containerItemProps = {
    className: "u-width-input-60px u-text-input-centre u-mr-3",
    style: { width: "auto" }
};

const processValueOnChange = (
    value: string,
    setValue: (value: string) => void,
    timeFormat?: string,
): void => {
    if (value && value.length > 2) {
        value = value.substring(0, 2);
    }
    const max =
        timeFormat === "24"
            ? "23"
            : timeFormat === "12"
            ? "12"
            : "59";
    // Max hour and minute
    if (value && parseInt(value) > parseInt(max)) {
        value = max;
    }
    // Min hour for the 12 hour format
    if (value && value === "00" && timeFormat === "12") {
        value = "12";
    }
    setValue(value);
};

const formatValueOnBlur = (
    value: string,
    setValue: (value: string) => void,
    setIsFormatted: (value: boolean) => void,
    timeformat?: string
): void => {
    if (value && value.length == 1
        && ( timeformat !== "12" || (timeformat === "12" && value !== "0"))) {
        setValue("0" + value);
    } else if (timeformat === "12" && value === "0") {
        setValue("12");
    } else {
        setValue(value);
    }
    setIsFormatted(true);
};

const Time = ({
    context,
    isRequired,
    errorMessage,
    inputs = [defaultinputs],
    name = "",
    value = {},
    timeFormat,
    timeFormatChoices = timeFormats,
    ...props
}: Props) => {
    const loaded = useRef(false);
    const [error, setError] = useState<string>(props.error || "");
    const [selectedHour, setSelectedHour] = useState<string>(value[0] || "");
    const [selectedMinute, setSelectedMinute] = useState<string>(value[1] || "");
    const shortenedTime = timeFormat && timeFormat === "12";
    const [selectedTimeFormat, setSelectedTimeFormat] = useState<iChoice>(
        value[2] || {
            text: "",
            value: ""
        }
    );
    const [isFormatted, setIsFormatted] = useState(false);

    const invalidTime = errorMessage
        ? errorMessage
        : "Every field has to be filled.";

    const onChangeEvent = () => {
        if (loaded.current) {
            context?.unsetValue(name);
            const value = [selectedHour, selectedMinute];
            shortenedTime && value.push(selectedTimeFormat.value);
            props.onChange && props.onChange(value);
            if (!isFormatted) {
                setError("");
            } else {
                setIsFormatted(false);
            }
        } else {
            loaded.current = true;
        }
    };

    const onBlurEvent = () => {
        /* istanbul ignore else */
        if (
            !selectedHour ||
            !selectedMinute ||
            (shortenedTime && !selectedTimeFormat.value)
        ) {
            invalidTime && setError(invalidTime);
        }

        /* istanbul ignore else */
        if (
            !isRequired &&
            !selectedHour &&
            !selectedMinute &&
            (!shortenedTime || (shortenedTime && !selectedTimeFormat.value))
        ) {
            setError("");
        }
    };

    const onFocusEvent = () => {
        setError("");
    };

    useEffect(onChangeEvent, [
        selectedHour,
        selectedMinute,
        selectedTimeFormat
    ]);

    return (
        <Container isRequired={isRequired} {...props}>
            {/*
            // @ts-ignore */}
            <StyledRowContainer data-id={props.id} justifyContent="flex-start">
                <StyledRowContainerItem {...containerItemProps}>
                    <LabelContainer>
                        <label
                            htmlFor={`${props.id}_hours`}
                            className="field-sub-label"
                        >
                            {inputs[0].customLabel || inputs[0].label}
                        </label>
                    </LabelContainer>
                    <NumberInput
                        id={`${props.id}_hours`}
                        placeholder={inputs[0].placeholder}
                        isRequired={isRequired}
                        value={selectedHour || inputs[0].defaultValue || ""}
                        error={error}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                            formatValueOnBlur(
                                e.target.value,
                                setSelectedHour,
                                setIsFormatted,
                                timeFormat
                            );
                            onBlurEvent();
                        }}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            setSelectedHour(e.target.value);
                            onFocusEvent();
                        }}
                        onChange={(hour: string) => {
                            processValueOnChange(hour, setSelectedHour, timeFormat);
                        }}
                        cssClass="integer-only keep-leading-zeros"
                    />
                </StyledRowContainerItem>

                <StyledRowContainerItem {...containerItemProps}>
                    <LabelContainer>
                        <label
                            htmlFor={`${props.id}_minutes`}
                            className="field-sub-label"
                        >
                            {inputs[1].customLabel || inputs[1].label}
                        </label>
                    </LabelContainer>
                    <NumberInput
                        id={`${props.id}_minutes`}
                        placeholder={inputs[1].placeholder}
                        isRequired={isRequired}
                        value={selectedMinute || inputs[1].defaultValue || ""}
                        error={error}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                            formatValueOnBlur(e.target.value, setSelectedMinute, setIsFormatted);
                            onBlurEvent();
                        }}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            setSelectedMinute(e.target.value);
                            onFocusEvent();
                        }}
                        onChange={(minute: string) => {
                            processValueOnChange(minute, setSelectedMinute);
                        }}
                        cssClass="integer-only keep-leading-zeros"
                    />
                </StyledRowContainerItem>

                {timeFormat && timeFormat === "12" ? (
                    <StyledRowContainerItem className="u-width-100px">
                        <Select
                            id={`${props.id}_time_format`}
                            placeholder={timeFormatPlaceholder}
                            isRequired={isRequired}
                            value={selectedTimeFormat.text}
                            error={error}
                            choices={[...timeFormatChoices].map(timeFormat => ({
                                text: timeFormat,
                                value: timeFormat
                            }))}
                            onBlur={() => {
                                onBlurEvent();
                            }}
                            onChange={(timeFormat) => {
                                setSelectedTimeFormat({
                                    text: timeFormat,
                                    value: timeFormat
                                });
                            }}
                            onFocus={() => {
                                onFocusEvent();
                            }}
                            cssClass="u-pt-5"
                            aria-label="Choose between 12 or 24 hour format"
                        />
                    </StyledRowContainerItem>
                ) : (
                    <></>
                )}
            </StyledRowContainer>
            <StyledRowError className="field-error">{error}</StyledRowError>
        </Container>
    );
};

const timeFormats = ["AM", "PM"];
const timeFormatPlaceholder = "--";
const defaultinputs = {
    customLabel: "",
    defaultValue: "",
    id: "",
    label: "",
    name: "",
    placeholder: ""
};

export default Time;
